// react
import React, { forwardRef, ReactNode, Ref } from 'react';

// mui
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Slide,
  SlideProps,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// components
import { Icon } from '@/components/atoms/Icon/Icon';

const Transition = forwardRef(function Transition(
  props: SlideProps,
  ref: Ref<unknown>,
) {
  return <Slide direction='up' ref={ref} {...props} />;
});

export interface BottomSheetDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Optional fixed footer, e.g. a primary CTA. */
  footer?: ReactNode;
}

const mobileBottomSheetPaper = {
  width: '100%',
  maxWidth: '100%',
  m: 0,
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
  overflow: 'hidden',
} as const;

/**
 * BottomSheetDrawer is the shared responsive drawer used across the Power of
 * Attorney flow: a bottom sheet on mobile and a centered dialog on larger
 * screens. It owns the Dialog mechanics so content drawers stay declarative.
 * Mirrors the established LPATypesDrawer pattern.
 */
export const BottomSheetDrawer = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
}: BottomSheetDrawerProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Mobile sheet spans the screen minus the app bar (spacing(12) = 96px).
  const sheetHeight = `calc(100dvh - ${theme.spacing(12)})`;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth={isMobile ? false : 'sm'}
      scroll='paper'
      {...(isMobile ? { TransitionComponent: Transition } : {})}
      sx={
        isMobile
          ? { '& .MuiDialog-container': { alignItems: 'flex-end' } }
          : undefined
      }
      slotProps={{
        paper: {
          sx: isMobile
            ? {
                ...mobileBottomSheetPaper,
                height: sheetHeight,
                maxHeight: sheetHeight,
              }
            : undefined,
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          pt: 3,
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Typography variant='h2' sx={{ fontSize: 20 }}>
          {title}
        </Typography>
        <IconButton onClick={onClose} size='small' aria-label='Close'>
          <Icon icon='xmark' fontSize={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, pb: footer ? 2 : 3 }}>
        {children}
      </DialogContent>
      {footer && (
        <DialogContent sx={{ px: 3, pb: 3, flex: '0 0 auto' }}>
          {footer}
        </DialogContent>
      )}
    </Dialog>
  );
};

BottomSheetDrawer.displayName = 'BottomSheetDrawer';
export default BottomSheetDrawer;
